import { useMemo } from "react";
import type { Passport } from "@oshimap/domain";
import { stableRotation, ROTATION_MAX } from "@oshimap/utils";
import { EmptyState } from "../../components/ui";
import "./passport.css";

/**
 * OD-007: KEEP / DEFERRED.
 * 국가별 집계는 현재 스키마로 불가능하다 (DM-11 / ADR-001 OPEN).
 * 그래서 **추측으로 국가를 만들어내지 않고**, 확실히 아는 것만 보여준다:
 * 방문 좌표와 지역 집계. 국가 축은 ADR-001 이 닫힌 뒤 붙인다.
 */
export function WorldView({ passport }: { passport: Passport }) {
  const points = passport.visitedMap;

  const spread = useMemo(() => {
    if (points.length === 0) return null;
    const lats = points.map((p) => p.latitude);
    const lngs = points.map((p) => p.longitude);
    return {
      north: Math.max(...lats), south: Math.min(...lats),
      east: Math.max(...lngs), west: Math.min(...lngs),
    };
  }, [points]);

  if (points.length === 0) {
    return (
      <div className="om-passport__pad">
        <div className="om-globe om-globe--empty" aria-hidden="true"><span>◍</span></div>
        <EmptyState
          title="지구본이 아직 어둡습니다"
          body="첫 방문을 기록하면 다녀온 자리에 불이 켜집니다."
        />
      </div>
    );
  }

  return (
    <div className="om-passport__pad">
      <div className="om-globe" role="img"
        aria-label={`방문한 ${points.length}곳이 표시된 지도`}>
        {points.map((p) => {
          const pos = project(p, spread!);
          return (
            <span key={p.id} className="om-globe__pin" style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
              title={p.name} />
          );
        })}
        <span className="om-globe__badge">☾ DEFERRED · GLOBAL</span>
      </div>

      {/*
        * 셋이 서로 다른 것을 세야 한다. 예전에는 "방문 지점" 과 "총 방문" 이
        * 둘 다 `totalPlaces` 여서 같은 숫자가 다른 이름으로 두 번 있었다.
        *   방문 지점 = 지도에 찍힌 서로 다른 장소
        *   지역     = 서로 다른 지역
        *   총 방문   = 방문 기록 수 (같은 곳을 또 가면 늘어난다)
        */}
      <div className="om-worldstats">
        <div><b className="num">{points.length}</b><span>방문 지점</span></div>
        <div><b className="num">{passport.cities.length}</b><span>지역</span></div>
        <div><b className="num">{passport.timeline.length}</b><span>총 방문</span></div>
      </div>

      {/* 8개를 넘으면 잘랐다는 사실을 밝힌다. 말없이 빠지면 안 간 것처럼 보인다. */}
      <h2 className="om-section__title" style={{ padding: 0, marginTop: "var(--s-6)" }}>
        다녀온 지역
        {passport.cities.length > 8
          ? <span className="om-count num"> 많이 간 8 / {passport.cities.length}</span>
          : null}
      </h2>
      <div className="om-cstamps">
        {passport.cities.slice(0, 8).map((c) => {
          const rot = stableRotation(c.name, ROTATION_MAX.countryStamp);
          return (
            <div key={c.name} className="om-cstamp" style={{ transform: `rotate(${rot.toFixed(2)}deg)` }}>
              <span className="om-cstamp__name">{c.name}</span>
              {/* 실측: 방문 횟수다 (장소 수가 아니다). */}
              <span className="om-cstamp__count num">{c.count}번</span>
            </div>
          );
        })}
      </div>

      <p className="om-note" style={{ marginTop: "var(--s-5)" }}>
        국가 단위 집계와 입국 도장은 <b>ADR-001</b> 이 닫힌 뒤 붙입니다.
        현재 <code>places</code> 에 국가를 알 수 있는 필드가 없어, 있는 것처럼 보이게 만들지 않았습니다.
      </p>
    </div>
  );
}

/** 방문 지점의 상대 위치. 좌표가 하나뿐이면 가운데에 둔다. */
function project(
  p: { latitude: number; longitude: number },
  b: { north: number; south: number; east: number; west: number },
): { x: number; y: number } {
  const dLat = b.north - b.south;
  const dLng = b.east - b.west;
  const pad = 18;
  const span = 100 - pad * 2;
  const x = dLng === 0 ? 50 : pad + ((p.longitude - b.west) / dLng) * span;
  const y = dLat === 0 ? 50 : pad + ((b.north - p.latitude) / dLat) * span;
  return { x, y };
}
